<template>
  <div>
    <!-- Header Section -->
    <div class="row mb-4">
      <div class="col-md-8">
        <div class="d-flex align-items-center">
          <div class="bg-primary-light p-3 rounded-2 me-3">
            <vue-feather type="calendar" class="d-flex justify-content-center align-content-center" stroke="#652aa3" size="24"></vue-feather>
          </div>
          <div>
            <h3 class="mb-0 fw-bold">My Leaves</h3>
          </div>
        </div>
      </div>
      <div class="col-md-4">
        <div class="d-flex justify-content-end">
          <button
            class="btn btn-primary shadow-sm"
            @click="openAddModal"
          >
            <vue-feather type="plus" size="18" class="me-1"></vue-feather>
            New Leave Request
          </button>
        </div>
      </div>
    </div>

    <!-- Leave Balances Section -->
    <div class="row mb-4">
      <div class="col-12">
        <Card cardClasses="shadow-sm border-0 h-100">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div class="font-size-125 color-gray-600">
              <vue-feather type="calendar" stroke="#652aa3" size="18" class="me-1"></vue-feather>
              Leave Balances
            </div>
          </div>

          <!-- Loading Spinner -->
          <div v-if="loadingBalances" class="text-center py-4">
            <Spinner spinner-class="mb-1" />
          </div>

          <!-- Leave Balances Cards -->
          <div v-if="!loadingBalances && leaveBalances.length > 0" class="row">
            <div class="col-md-4 mb-3" v-for="balance in leaveBalances" :key="balance.id">
              <div class="card h-100 border-0 shadow-sm">
                <div class="card-body">
                  <h5 class="card-title">{{ balance.leave_type.name }} - {{ balance.year }}</h5>
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <div class="fw-bold">Entitled:</div>
                    <div>{{ balance.entitled_days }} days</div>
                  </div>
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <div class="fw-bold">Used:</div>
                    <div>{{ balance.used_days }} days</div>
                  </div>
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <div class="fw-bold">Pending:</div>
                    <div>{{ balance.pending_days }} days</div>
                  </div>
                  <div class="d-flex justify-content-between align-items-center">
                    <div class="fw-bold">Remaining:</div>
                    <div>
                      <span :class="getRemainingDaysClass(balance)">
                        {{ balance.entitled_days - balance.used_days - balance.pending_days }} days
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-if="!loadingBalances && leaveBalances.length === 0" class="text-center py-4">
            <vue-feather type="calendar" size="48" class="mb-3" stroke="#652aa3"></vue-feather>
            <h5>No Leave Balances Found</h5>
            <p class="text-muted">No leave balances have been allocated for this employee in {{ selectedYear }}.</p>
          </div>
        </Card>
      </div>
    </div>

    <!-- Leave Requests Section -->
    <div class="row">
      <div class="col-12">
        <Card cardClasses="shadow-sm border-0 h-100 mb-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div class="font-size-125 color-gray-600">
              <vue-feather type="calendar" stroke="#652aa3" size="18" class="me-1"></vue-feather>
              Leave Requests
            </div>
            <UInput v-if="!loadingRequests && leaveRequests.length > 0" v-model="dataTableSearch" size="md" placeholder="Search leave request" class="mb-0 w-25 search-input"/>
          </div>

          <!-- Loading Spinner -->
          <div v-if="loadingRequests" class="text-center py-4">
            <Spinner spinner-class="mb-1" />
          </div>

          <!-- Leave Requests Table -->
          <div v-if="!loadingRequests && leaveRequests.length > 0" class="table-responsive">
            <table class="table table-hover align-middle">
              <thead class="table-light">
                <tr>
                  <th scope="col" class="fw-semibold">#</th>
                  <th scope="col" class="fw-semibold">Leave Type</th>
                  <th scope="col" class="fw-semibold">Start Date</th>
                  <th scope="col" class="fw-semibold">End Date</th>
                  <th scope="col" class="fw-semibold">Days</th>
                  <th scope="col" class="fw-semibold">Status</th>
                  <th scope="col" class="fw-semibold">Applied On</th>
                  <th scope="col" class="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(request, index) in filteredLeaveRequests" :key="request.id">
                  <td>{{ index + 1 }}</td>
                  <td>{{ request.leave_type.name }}</td>
                  <td>{{ formatDate(request.start_date) }}</td>
                  <td>{{ formatDate(request.end_date) }}</td>
                  <td>{{ request.days }}</td>
                  <td>
                    <span :class="getStatusBadgeClass(request.status)">
                      {{ capitalizeFirstLetter(request.status) }}
                    </span>
                  </td>
                  <td>{{ formatDate(request.created_at) }}</td>
                  <td class="text-end">
                    <button class="btn btn-sm btn-outline-primary me-1" @click="viewLeaveRequest(request)">
                      <vue-feather type="eye" size="14"></vue-feather>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Empty State -->
          <div v-if="!loadingRequests && leaveRequests.length === 0" class="text-center py-5">
            <vue-feather type="calendar" size="48" class="mb-3" stroke="#652aa3"></vue-feather>
            <h5>No Leave Requests Found</h5>
            <p class="text-muted">This employee has not made any leave requests yet.</p>
            <button class="btn btn-primary" @click="openAddModal">
              <vue-feather type="plus" size="16" class="me-1"></vue-feather>
              New Leave Request
            </button>
          </div>
        </Card>
      </div>
    </div>

    <!-- Add Leave Request Modal -->
    <Modal
        :open="showAddModal"
        @closed="closeAddModal"
    >
      <div class="modal-header border-0">
        <h5 class="modal-title m-0 d-flex align-items-center">
          New Leave Request
        </h5>
        <button @click="closeAddModal" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="">
          <div class="mb-3">
            <label for="leave_type_id" class="form-label">Leave Type <span class="text-danger">*</span></label>
            <select class="form-select" id="leave_type_id" v-model="form.leave_type_id" required data="">
              <option value="" disabled>Select Leave Type</option>
              <option v-for="type in leaveTypes.filter(t => t.is_active)" :key="type.id" :value="type.id">
                {{ type.name }}
              </option>
            </select>
          </div>
          <div class="mb-3">
            <label for="start_date" class="form-label">Start Date <span class="text-danger">*</span></label>
            <input type="date" class="form-control" id="start_date" v-model="form.start_date" required @change="calculateDays">
          </div>
          <div class="mb-3">
            <label for="end_date" class="form-label">End Date <span class="text-danger">*</span></label>
            <input type="date" class="form-control" id="end_date" v-model="form.end_date" required @change="calculateDays">
          </div>
          <div class="mb-3">
            <label for="days" class="form-label">Days</label>
            <input type="number" class="form-control" id="days" v-model="calculatedDays" readonly>
          </div>
          <div class="mb-3">
            <label for="reason" class="form-label">Reason</label>
            <textarea class="form-control" id="reason" v-model="form.reason" rows="3"></textarea>
          </div>
          <div class="mb-3">
            <label for="attachment" class="form-label">Attachment</label>
            <input type="file" class="form-control" @input="handleFileUpload" name="attachment" accept=".jpeg,.png,.jpg,.pdf">
            <small class="text-muted">Supported formats: JPEG, PNG, JPG, PDF</small>
          </div>
          <div class="d-flex justify-content-end">
            <button type="button" class="btn btn-outline-secondary me-2" @click="closeAddModal">Cancel</button>
            <button type="submit" class="btn btn-primary" @click="saveLeaveRequest" :class="saving ? 'disabled' : ''">
              <span v-if="saving" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              Submit
            </button>
          </div>
        </form>
      </div>
    </Modal>

    <!-- View Leave Request Modal -->
    <Modal
      :open="showViewModal"
      @closed="closeViewModal"
      dialog-size="modal-lg"
    >
      <div class="modal-header border-0">
        <h5 class="modal-title m-0 d-flex align-items-center">
          Leave Request Details
        </h5>
        <button @click="closeViewModal" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <div v-if="selectedRequest">
          <div class="mb-3">
            <h5 class="mb-3">Leave Information</h5>
            <div class="row">
              <div class="col-md-6 mb-2">
                <strong>Leave Type:</strong> {{ selectedRequest.leave_type.name }}
              </div>
              <div class="col-md-6 mb-2">
                <strong>Status: </strong>
                <span :class="getStatusBadgeClass(selectedRequest.status)">
                  {{ capitalizeFirstLetter(selectedRequest.status) }}
                </span>
              </div>
              <div class="col-md-6 mb-2">
                <strong>Start Date:</strong> {{ formatDate(selectedRequest.start_date) }}
              </div>
              <div class="col-md-6 mb-2">
                <strong>End Date:</strong> {{ formatDate(selectedRequest.end_date) }}
              </div>
              <div class="col-md-6 mb-2">
                <strong>Days:</strong> {{ selectedRequest.days }}
              </div>
              <div class="col-md-6 mb-2">
                <strong>Applied On:</strong> {{ formatDate(selectedRequest.created_at) }}
              </div>
              <div class="col-12 mb-2">
                <strong>Reason:</strong> {{ selectedRequest.reason || 'N/A' }}
              </div>
              <div class="col-12 mb-2" v-if="selectedRequest.attachment">
                <strong>Attachment:</strong>
                <a :href="getAttachmentUrl(selectedRequest.attachment)" target="_blank">View Attachment</a>
              </div>
            </div>
          </div>

          <hr v-if="selectedRequest.status === 'rejected'">

          <div class="mb-3" v-if="selectedRequest.status === 'rejected'">
            <h5 class="mb-3">Rejection Information</h5>
            <div class="row">
              <div class="col-md-6 mb-2">
                <strong>Rejected By:</strong> {{ selectedRequest.approver?.first_name }} {{ selectedRequest.approver?.last_name }}
              </div>
              <div class="col-md-6 mb-2">
                <strong>Rejected On:</strong> {{ formatDate(selectedRequest.approved_at) }}
              </div>
              <div class="col-12 mb-2">
                <strong>Reason:</strong> {{ selectedRequest.rejection_reason || 'N/A' }}
              </div>
            </div>
          </div>

          <div class="d-flex justify-content-end mt-4">
            <button type="button" class="btn btn-outline-secondary" @click="closeViewModal">Close</button>
          </div>
        </div>
      </div>
    </Modal>

    <!-- Reject Leave Request Modal -->
    <Modal
      title="Reject Leave Request"
      :open="showRejectModal"
      @closed="closeRejectModal"
    >
      <form @submit.prevent="rejectLeaveRequest">
        <div class="mb-3">
          <label for="rejection_reason" class="form-label">Reason for Rejection <span class="text-danger">*</span></label>
          <textarea class="form-control" id="rejection_reason" v-model="rejectForm.rejection_reason" rows="3" required></textarea>
        </div>
        <div class="d-flex justify-content-end">
          <button type="button" class="btn btn-outline-secondary me-2" @click="closeRejectModal">Cancel</button>
          <button type="submit" class="btn btn-danger" :disabled="saving">
            <span v-if="saving" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
            Reject
          </button>
        </div>
      </form>
    </Modal>
  </div>
</template>

<script>
import leaveService from '@/api/leaveService';
import {decrypt} from '@/utils/crypto';
import notify from '@/utils/notify';
import formatDate from '@/utils/formatDate';
import VueFeather from 'vue-feather';

export default {
  name: 'MyLeaves',
  components: { VueFeather },
  data() {
    return {
      loading: true,
      loadingBalances: true,
      loadingRequests: true,
      saving: false,
      leaveRequests: [],
      leaveBalances: [],
      leaveTypes: [],
      dataTableSearch: '',
      selectedYear: new Date().getFullYear(),
      showAddModal: false,
      showViewModal: false,
      showRejectModal: false,
      selectedRequest: null,
      profile: {},
      organization: {},
      form: {
        employee_id: '',
        leave_type_id: '',
        start_date: '',
        end_date: '',
        reason: '',
        attachment: null
      },
      rejectForm: {
        rejection_reason: ''
      },
      calculatedDays: 0,
    };
  },
  computed: {
    filteredLeaveRequests() {
      if (!this.dataTableSearch) return this.leaveRequests;

      const search = this.dataTableSearch.toLowerCase();
      return this.leaveRequests.filter(request =>
        request.leave_type.name.toLowerCase().includes(search) ||
        request.status.toLowerCase().includes(search)
      );
    }
  },
  methods: {
    formatDate,
    capitalizeFirstLetter(string) {
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
    getStatusBadgeClass(status) {
      switch (status) {
        case 'pending':
          return 'badge bg-warning';
        case 'approved':
          return 'badge bg-success';
        case 'rejected':
          return 'badge bg-danger';
        case 'cancelled':
          return 'badge bg-secondary';
        default:
          return 'badge bg-primary';
      }
    },
    getRemainingDaysClass(balance) {
      const remaining = balance.entitled_days - balance.used_days - balance.pending_days;
      if (remaining <= 0) {
        return 'badge bg-danger';
      } else if (remaining < 5) {
        return 'badge bg-warning';
      } else {
        return 'badge bg-success';
      }
    },
    getAttachmentUrl(attachment) {
      return this.apiImageUrl + 'leave-attachments/' + attachment;
    },
    async fetchLeaves() {
      try {
        this.loadingRequests = true;
        this.loadingBalances = true;
        let response = await leaveService.getEmployeeLeaves(this.profile.employee_id);
        this.leaveRequests = response.leaves;
        this.leaveBalances = response.balances;
      } catch (error) {
        notify.alert.error('Failed to fetch leave requests');
        console.error(error);
      } finally {
        this.loadingRequests = false;
        this.loadingBalances = false;
      }
    },
    async fetchLeaveTypes() {
      try {
        this.leaveTypes = await leaveService.getLeaveTypes();
      } catch (error) {
        notify.alert.error('Failed to fetch leave types');
        console.error(error);
      }
    },
    calculateDays() {
      if (!this.form.start_date || !this.form.end_date) {
        this.calculatedDays = 0;
        return;
      }

      const start = new Date(this.form.start_date);
      const end = new Date(this.form.end_date);

      if (end < start) {
        notify.alert.error('End date cannot be before start date');
        this.form.end_date = this.form.start_date;
        this.calculatedDays = 1;
        return;
      }

      // Calculate business days considering organization weekend settings
      let days = 0;
      const current = new Date(start);

      const workSaturday = !!(this.organization && this.organization.work_saturday);
      const workSunday = !!(this.organization && this.organization.work_sunday);

      while (current <= end) {
        const dayOfWeek = current.getDay(); // 0 Sun ... 6 Sat
        const isWeekday = dayOfWeek >= 1 && dayOfWeek <= 5;
        const isSaturday = dayOfWeek === 6;
        const isSunday = dayOfWeek === 0;

        if (
          isWeekday ||
          (isSaturday && workSaturday) ||
          (isSunday && workSunday)
        ) {
          days++;
        }
        current.setDate(current.getDate() + 1);
      }

      this.calculatedDays = days;
    },
    openAddModal() {
      this.form = {
        employee_id: this.profile.employee_id,
        leave_type_id: '',
        start_date: '',
        end_date: '',
        reason: '',
        attachment: null
      };
      this.calculatedDays = 0;
      this.showAddModal = true;
    },
    closeAddModal() {
      this.showAddModal = false;
    },
    viewLeaveRequest(request) {
      this.selectedRequest = request;
      this.showViewModal = true;
    },
    closeViewModal() {
      this.showViewModal = false;
      this.selectedRequest = null;
    },
    openRejectModal(request) {
      this.selectedRequest = request;
      this.rejectForm.rejection_reason = '';
      this.showRejectModal = true;
    },
    closeRejectModal() {
      this.showRejectModal = false;
      this.selectedRequest = null;
    },
    async saveLeaveRequest() {
      if (!this.form.leave_type_id || !this.form.start_date || !this.form.end_date) {
        notify.alert.error('Please fill all required fields');
        return;
      }

      try {
        this.saving = true;

        const formData = new FormData();
        formData.append('employee_id', this.form.employee_id);
        formData.append('leave_type_id', this.form.leave_type_id);
        formData.append('start_date', this.form.start_date);
        formData.append('end_date', this.form.end_date);
        formData.append('reason', this.form.reason || '');

        if (this.form.attachment) {
          let fileReader = this.processFile(this.form.attachment)
          fileReader.onload = async () => {
            if (typeof fileReader.result === 'string')
              formData.append('attachment', fileReader.result);
            await leaveService.createLeaveRequest(formData);
            this.closeAddModal();
            await this.fetchLeaves();
          }
        } else {
          await leaveService.createLeaveRequest(formData);
          this.closeAddModal();
          await this.fetchLeaves();
        }
      } catch (error) {
        notify.alert.error(error.response?.data?.message || 'Failed to create leave request');
        console.error(error);
      } finally {
        this.saving = false;
      }
    },
    handleFileUpload(file) {
      this.form.attachment = file
    },
    processFile : function(file) {
      const fileReader = new FileReader()
      const { files } = file.target
      if (files && files.length) {
        fileReader.readAsDataURL(files[0])
        return fileReader
      }
    },
    approveLeaveRequest(request) {
      this.selectedRequest = request;
      this.confirmAction = 'approve';
      this.$refs.confirmModal.show();
    },
    cancelLeaveRequest(request) {
      this.selectedRequest = request;
      this.$refs.confirmModal.show();
    },
    async rejectLeaveRequest() {
      if (!this.rejectForm.rejection_reason) {
        notify.alert.error('Please provide a reason for rejection');
        return;
      }

      try {
        this.saving = true;
        await leaveService.rejectLeaveRequest(this.selectedRequest.id, this.rejectForm);
        this.closeRejectModal();
        await this.fetchLeaves();
      } catch (error) {
        notify.alert.error(error.response?.data?.message || 'Failed to reject leave request');
        console.error(error);
      } finally {
        this.saving = false;
      }
    }
  },
  created() {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')));
    this.organization = JSON.parse(decrypt(sessionStorage.getItem('organization')));
    this.fetchLeaves();
    this.fetchLeaveTypes();
  }
};
</script>
